import { useToast } from '@monorepo/shared/contexts';
import { useConfirmIncompleteLogEntries } from '@monorepo/shared/hooks/logs/useConfirmIncompleteLogEntries';
import { useLogEntryAiSubmit } from '@monorepo/shared/hooks/logs/useLogEntryAiSubmit';
import { ValidationErrors } from 'final-form';
import { FieldResponse } from 'mapistry-shared';
import { useCallback, useState } from 'react';
import {
  convertLogEntriesFormFieldValuesToSaveLogEntryRequests,
  LogEntriesFormValues,
} from '../conversionHelpers';
import { type UploadedFile } from './useUploadedFiles';

type UseLogEntriesSubmissionProps = {
  organizationId: string;
  projectId: string;
  logId: string;
  selectedFile: UploadedFile | undefined;
  logFields: FieldResponse[] | undefined;
  fileCompleted: (requestId: string) => void;
};

export function useLogEntriesSubmission({
  organizationId,
  projectId,
  logId,
  selectedFile,
  logFields,
  fileCompleted,
}: UseLogEntriesSubmissionProps) {
  const { showUserFriendlyErrorToast, success: showSuccessToast } = useToast();
  const { submitWithIncompleteChecks } = useConfirmIncompleteLogEntries();
  const [submitter] = useLogEntryAiSubmit({ config: { throwOnError: true } });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFileSubmitted, setIsFileSubmitted] = useState(false);

  const clearFileSubmitted = useCallback(() => {
    setIsFileSubmitted(false);
  }, []);

  const submitLogEntries = useCallback(
    async (formValues: LogEntriesFormValues) => {
      if (
        !selectedFile?.logEntries?.length ||
        !selectedFile.processedFile ||
        !selectedFile.requestId ||
        !logFields
      )
        return;
      const successCount = selectedFile.logEntries.length;
      const noun = successCount === 1 ? 'Log entry' : 'Log entries';
      try {
        const saveLogEntryRequests = Object.values(
          convertLogEntriesFormFieldValuesToSaveLogEntryRequests({
            logEntries: selectedFile.logEntries,
            formValues,
            logFields,
          }),
        );
        setIsSubmitting(true);
        await submitter({
          organizationId,
          projectId,
          logId,
          request: {
            saveLogEntryRequests: saveLogEntryRequests.map((req) => ({
              ...req,
              id: undefined,
            })),
            edpAiSubmission: { files: [selectedFile.processedFile] },
          },
        });
        fileCompleted(selectedFile.requestId);
        setIsSubmitting(false);
        showSuccessToast(
          `Successfully saved ${successCount} ${noun}! The compliance calendar may take a few minutes to reflect these changes.`,
        );
        setIsFileSubmitted(true);
      } catch (submitError) {
        setIsSubmitting(false);
        showUserFriendlyErrorToast(submitError, `Unable to save ${noun}.`, {
          dontAutoHide: true,
        });
      }
    },
    [
      selectedFile,
      logFields,
      logId,
      organizationId,
      projectId,
      showSuccessToast,
      showUserFriendlyErrorToast,
      submitter,
      fileCompleted,
    ],
  );

  const handleSubmit = useCallback(
    async (formValues: LogEntriesFormValues, errors: ValidationErrors) => {
      if (
        !selectedFile?.logEntries?.length ||
        !selectedFile?.processedFile ||
        !logFields ||
        isSubmitting
      )
        return;

      const entriesErrors = selectedFile.logEntries.map(
        (entry) => errors?.[entry.id] ?? {},
      );
      await submitWithIncompleteChecks({
        entriesErrors,
        submit: () => submitLogEntries(formValues),
      });
    },
    [
      selectedFile,
      isSubmitting,
      logFields,
      submitLogEntries,
      submitWithIncompleteChecks,
    ],
  );

  return {
    handleSubmit,
    isSubmitting,
    isFileSubmitted,
    clearFileSubmitted,
  };
}
